import { cn } from '@/shared/helpers/classnames'

import type { FC } from 'react'

import type { OptionLabelProps } from './types'

/**
 * OptionLabel component for wrapping text content in form options.
 *
 * Used to provide consistent text styling for options in RadioGroup, Checkbox, and Toggle components.
 * Handles different text sizes based on the size prop.
 *
 * @example
 * Basic usage with RadioGroup:
 * ```tsx
 * <RadioGroup.Option value="option1">
 *   <OptionLabel>Option 1</OptionLabel>
 * </RadioGroup.Option>
 * ```
 *
 * @example
 * With custom size:
 * ```tsx
 * <RadioGroup.Option value="option1">
 *   <OptionLabel size="lg">Large Option</OptionLabel>
 * </RadioGroup.Option>
 * ```
 *
 * @example
 * With Checkbox:
 * ```tsx
 * <InputLabel>
 *   <Checkbox checked={value} onChange={handleChange} />
 *   <OptionLabel size="sm">Small label</OptionLabel>
 * </InputLabel>
 * ```
 */
export const OptionLabel: FC<OptionLabelProps> = ({ children, size = 'md' }) => {
  return (
    <span
      className={
        'leading-none ' + // conflict tailwind that removes leading
        cn('whitespace-nowrap', {
          'text-sm': size === 'sm',
          'text-base': size === 'md',
          'text-lg': size === 'lg',
        })
      }
    >
      {children}
    </span>
  )
}

OptionLabel.displayName = 'OptionLabel'
